import { Await, Link, useLocation, useRouteLoaderData } from '@remix-run/react';
import { SlidersHorizontal } from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef } from 'react'
import { getCountryName, getFormattedCountryName } from '~/lib/helpers/constants';
import { getCurrentCustomerCount, parseSearchUrl } from '~/lib/helpers/StringHelper';
import {
  clearStaleSearchPlaceContextForCityLevelPath,
  getPersistedSearchLocationContext,
} from '~/lib/helpers/searchPlaceContext';
import { buildLocationUrlWithOfficeTypeFilter, buildPropertyNavCountryUrl } from '~/components/blocks/Geosearch/locationGeosearch';
import { LazyLoader } from '~/lib/utils/LazyLoader';
import { ClientOnly } from '../common/client-only';
import { ListOnOfficeHub } from '../common/ListOnOfficeHub';
import OfficeSearchBanner from '../common/OfficeBanner';
import AutoPlaces from '../Geosearch/AutoPlaces';
import ProviderSliderSection from '../Sections/ProviderSliderSection';
import { AboutSection } from './AboutSection';
import { GoogleReviews } from './GoogleReviews/GoogleReviews';
import { GuideAndTips } from './GuideAndTips';
import { PopularLocations } from './PopularLocations';
import WhyOfficeHub from './WhyOfficeHub';
import WorkspaceFinder from './WorkspaceFinder';
import OfficeOptions from './WorkspaceOptions';
import Container from '~/components/atoms/Container';
import Drawer from '../Geosearch/Drawer';
import NearByProperties from '../common/NearByProperties';
import { getRegionUrlPrefix } from '~/lib/helpers/slugHelper';
import { AboutSectionSkeleton, BlogsSkeleton, FeaturedPlacesSkeleton, ListingsSkeleton, PartnersSkeleton, PropertiesSkeleton, StatsCounterSkeleton } from '~/components/ui/HomeSkeletons';
import { numberFormatter } from '~/lib/helpers/numberFormatHelper';
import { OhButton } from '~/components/ui/custom-radix-components/button';
import { useNavigate } from '@remix-run/react';
import { useLoading } from '~/lib/contexts/LoadingContext';
import { HappyClientsAvatars } from '../common/HappyClientsAvatars';
import PropertyGptSearchPromo from './PropertyGptSearchPromo';
import { cn } from '~/lib/utils';
import { HOME_PAGE_SECTION_IDS } from "./constants";
import FAQs from "../FAQs/FAQs";
import { buildHomeChatPageContext } from "./buildHomeChatPageContext";
// function DesktopLayout({ categories, countryName, searchUrl, drawer, setDrawer, url, query, callGeoSearch, filterData, setUrl, loaderData, filters, setIsLoading, setFilters, isLoading, region, userCurrency }) {
let timer = null
function DesktopLayout({
  categories,
  drawer,
  setDrawer,
  url,
  query,
  callGeoSearch,
  callGeoSearchForDrawer,
  onApplyDrawerFilters,
  filterData,
  setUrl,
  loaderData,
  region,
  countryName,
  searchUrl,
  countryCode,
  state,
  city,
  initialUserCurrency,
  geoData,
  maxPrice,
  parsedQuery,
  regionData
}) {
  const location = useLocation();
  const searchBarRef = useRef<HTMLDivElement | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { showSpinner } = useLoading();
  const showChatbot = (useRouteLoaderData("root") as { showChatbot?: boolean } | undefined)?.showChatbot;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (typeof window === "undefined") return;

      const payload = await buildHomeChatPageContext({
        region,
        countryName,
        pathname: location?.pathname,
        categories,
        initialUserCurrency,
        partners: loaderData?.partners,
        nearByProperties: loaderData?.nearByProperties,
        featuredPlaces: loaderData?.featuredPlaces,
        blogs: loaderData?.blogs,
        homeSummary: loaderData?.homeSummary,
      });

      if (cancelled) return;
      (window as any).__OH_CHAT_PAGE_CONTEXT__ = payload;
    })();

    return () => {
      cancelled = true;
    };
  }, [categories, loaderData, region, countryName, location?.pathname, initialUserCurrency]);

  const clearRedirectTimer = useCallback(() => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  }, []);

  const navigateToSearchUrl = (nextUrl: string) => {
    if (!nextUrl) return;
    clearRedirectTimer();
    const parsedUrl = new URL(nextUrl, window.location.origin);
    clearStaleSearchPlaceContextForCityLevelPath(parsedUrl.pathname);
    showSpinner();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        navigate(parsedUrl.pathname + parsedUrl.search);
      });
    });
  };
  // 👇 Observe when search bar leaves viewport
  useEffect(() => {
    if (!searchBarRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) {
          clearRedirectTimer();
        }
      },
      {
        root: null,
        threshold: 0.1, // consider "out of view" if less than 10% visible
      }
    );

    observer.observe(searchBarRef.current);

    return () => {
      observer.disconnect();
      clearRedirectTimer();
    };
  }, [clearRedirectTimer]);

  const combineUrlAndQuery = (baseUrl: string, queryString: string): string => {
    return buildLocationUrlWithOfficeTypeFilter(
      baseUrl,
      queryString,
      filterData?.officeTypes,
    );
  };

  const handleLocationSelect = (loc: string) => {
    clearRedirectTimer();
    if (loc) {
      // Ensure loc doesn't have trailing / before ? or at the end
      let cleanLoc = loc.replace(/\/\?/g, '?'); // Replace /? with ?
      cleanLoc = cleanLoc.replace(/\?$/, ''); // Remove trailing ?

      // Ensure path starts with / if it's a relative URL
      if (!cleanLoc.startsWith('http://') && !cleanLoc.startsWith('https://') && !cleanLoc.startsWith('/')) {
        cleanLoc = `/${cleanLoc}`;
      }

      // Only remove trailing / if it's not followed by query params
      if (cleanLoc.includes('?')) {
        const [path, urlQuery] = cleanLoc.split('?');
        cleanLoc = path.replace(/\/$/, '') + (urlQuery ? `?${urlQuery}` : '');
      } else {
        cleanLoc = cleanLoc.replace(/\/$/, '');
      }

      // Store as full URL if it's not already
      let urlToStore = cleanLoc;
      if (!cleanLoc.startsWith('http://') && !cleanLoc.startsWith('https://')) {
        urlToStore = `${window.location.origin}${cleanLoc.startsWith('/') ? cleanLoc : `/${cleanLoc}`}`;
      }

      setUrl(urlToStore);
      const parsed = parseSearchUrl(cleanLoc) as Record<string, any>;
      // Clean URLs no longer carry the coordinates, so fall back to the
      // background location context persisted when the place was selected.
      const ctx = getPersistedSearchLocationContext();
      const lat = parsed?.latitude ?? ctx?.latitude;
      const lng = parsed?.longitude ?? ctx?.longitude;
      const countryCode = parsed?.country ?? ctx?.country;
      if (lat && lng && countryCode) {
        // store cookie for 30 days
        document.cookie = `lastSearchedLocation=${encodeURIComponent(
          JSON.stringify({
            lat,
            lng,
            country: countryCode,
          })
        )}; path=/; max-age=${60 * 60 * 24 * 30}`;
        redirectTimerRef.current = setTimeout(() => {
          const finalUrl = combineUrlAndQuery(urlToStore, query);
          navigateToSearchUrl(finalUrl);
        }, 3000);
      }
    } else {
      setUrl(null)
    }

  };

  return (
    <div className="flex flex-col w-full justify-center montserrat">
      {/* Hero Section */}
      <Container htmlFor="fluid">
        <section id={HOME_PAGE_SECTION_IDS.hero} className="relative bg-white max-h-[740px] scroll-mt-28">
          <div className="absolute inset-0">
            <img
              src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/home.avif`}
              alt="Home"
              width={1920}
              height={1080}
              className='w-full h-full object-cover '
              loading="eager"
              fetchPriority='high'
            />
            {/* <div className="absolute inset-0" /> */}
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 py-40 flex flex-col items-start">
            <Suspense fallback={
              <p className="text-sm mb-2 px-4 py-1 bg-white/50 rounded-full border-white/60 border-2">
                <span className="text-oh-primary font-semibold">Finding</span> offices in your region...
              </p>}>
              <Await resolve={loaderData.homeSummary}>
                {(homeSummary) => (
                  <Link
                    to={buildPropertyNavCountryUrl(getRegionUrlPrefix(countryName))}
                    prefetch="intent"
                  >
                    <p className="text-sm mb-2 px-4 py-1 bg-white/50 rounded-full border-white/60 border-2">
                      <span className="text-oh-primary font-semibold">{homeSummary?.totalProperties < 100 ? '100' : homeSummary?.totalProperties?.toLocaleString()}+ </span>Flexible Offices Near You
                    </p>
                  </Link>
                )}
              </Await>
            </Suspense>

            <h1 className="text-[30px] font-bold text-gray-900 leading-tight mb-6 max-w-[550px]">
              Find & Rent Coworking, Serviced and Shared Offices in {getFormattedCountryName(region)}
            </h1>

            <div
              className={cn(
                "inline-flex flex-col max-w-full w-fit",
                showChatbot && "rounded-xl border shadow-sm",
              )}
            >
              <div
                className={cn(
                  "bg-white p-4 space-y-3 w-fit max-w-full overflow-visible",
                  showChatbot ? "rounded-t-xl" : "rounded-xl shadow-sm border",
                )}
              >
                {/* Category Pills */}
                <div className="flex flex-wrap items-center gap-2">
                  {categories.map((cat, i) => {

                    return (
                      <button
                        key={cat.value}
                        onClick={() => {
                          if (filterData?.officeTypes?.includes(cat.value)) {
                            if (cat.value === 'All') {
                              return
                            }
                            const filteredTypes = filterData?.officeTypes?.filter((i: string) => i !== cat.value)
                            callGeoSearch({ workspaceType: filteredTypes.join(',') })
                          } else {
                            if (cat.value !== 'All') {
                              const filteredTypes = [...filterData?.officeTypes, cat.value].filter(i => i !== 'All')
                              callGeoSearch({ workspaceType: filteredTypes.join(',') })
                            } else {
                              const filteredTypes = [cat.value]
                              callGeoSearch({ workspaceType: filteredTypes.join(',') })
                            }
                          }
                        }}
                        className={`px-4 py-1.5 text-sm rounded-full border transition ${filterData?.officeTypes?.includes(cat.value)
                          ? "border-oh-primary bg-teal-50 text-oh-primary"
                          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {cat.option}
                      </button>
                    )
                  })}
                  <button
                    aria-label="Filters"
                    onClick={() => {
                      clearRedirectTimer();
                      setDrawer(true);
                    }}
                    className="ml-auto p-2 rounded-full border border-gray-300 hover:bg-gray-100"
                  >
                    <SlidersHorizontal className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                {drawer && (
                  <Drawer
                    url={url ? combineUrlAndQuery(url, query) : undefined}
                    open={drawer}
                    setOpen={setDrawer}
                    callGeoSearch={callGeoSearchForDrawer}
                    filterData={filterData}
                    data={null}
                    workspace={null}
                    maxPrice={maxPrice}
                    onApply={onApplyDrawerFilters}
                    isHomePage
                  />
                )}

                {/* Search Bar */}
                <div ref={searchBarRef} className="relative z-20 flex items-center gap-2">
                  <div className="relative flex-1">
                    <ClientOnly>
                      {() => (
                        <AutoPlaces
                          onLocationSelect={handleLocationSelect}
                          inputStyleClassName="
                            rounded-full
                            bg-[#E8F7F8]
                            border-[#12AFBD]
                            text-slate-800
                            focus-visible:ring-0
                            focus-visible:border-[#12AFBD]"
                        />
                      )}
                    </ClientOnly>
                  </div>
                  <OhButton
                    onClick={() => {
                      clearRedirectTimer();
                      const finalUrl = combineUrlAndQuery(url, query);
                      if (!finalUrl) return;
                      navigateToSearchUrl(finalUrl);
                    }}
                    className="px-6 h-11 text-sm flex items-center justify-center rounded-full"
                    disabled={!url}
                  >
                    Search
                  </OhButton>
                </div>
              </div>

              <PropertyGptSearchPromo countryName={countryName} />
            </div>
            {/* Rating + Customers */}
            <Link
              to={`${getRegionUrlPrefix(countryName)}/testimonials`}
              target='blank'
              prefetch="intent"
            >
              <div className="flex items-center gap-3 mt-8">
                <HappyClientsAvatars imageClassName="w-8 h-8" />
                <Suspense fallback={null}>
                  <Await resolve={loaderData.homeSummary}>

                    {(homeSummary) => {
                      return (
                        <>
                          <span className="text-gray-900 text-sm font-semibold ">
                            {getCurrentCustomerCount()}+ Happy Clients
                          </span>
                          <div className="flex items-center bg-gray-100 px-2 py-1 rounded-md text-base font-semibold">
                            <img
                              src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/google-fav-logo.avif`}
                              alt="Google Logo"
                              title='Google'
                              className="w-6 h-6 mr-2"
                            />
                            ⭐ {parseFloat(homeSummary.sumRatings) > 0 ? parseFloat(homeSummary.sumRatings).toFixed(1) : '5.0'}
                          </div>
                        </>
                      );
                    }}
                  </Await>
                </Suspense>
              </div>
            </Link>
          </div>
        </section>
      </Container>

      <WorkspaceFinder region={region} country={getCountryName(region)} />

      <Suspense fallback={<PropertiesSkeleton />}>
        <Await resolve={loaderData.nearByProperties}>
          {(nearByProperties) => {
            return (
              nearByProperties && nearByProperties.length > 3 ?
                <Container htmlFor="ultra-wide">
                  <div id={HOME_PAGE_SECTION_IDS.topLocations} className="my-12 px-4 scroll-mt-28">
                    <NearByProperties carousel heading={`Top Office Locations in ${getFormattedCountryName(region)}`} subHeading={`Explore coworking, serviced and shared offices in ${getFormattedCountryName(region)}’s key business hubs.`} nearByProperties={nearByProperties} region={region} path={location.pathname} className={'flex overflow-x-auto scrollbar-hide'} />
                  </div>
                </Container>
                : null
            )
          }}
        </Await>
      </Suspense>

      <OfficeOptions region={region} country={getCountryName(region)} />

      <WhyOfficeHub region={region} country={getCountryName(region)} searchUrl={`${searchUrl}`} isMobile={false} />

      <Suspense fallback={<FeaturedPlacesSkeleton />}>
        <Await resolve={loaderData.featuredPlaces}>
          {(places) => {
            return places?.results.length > 5 ? (
              <div className="my-12 ">
                <PopularLocations places={places.results} region={region} />
              </div>
            ) : null
          }}
        </Await>
      </Suspense>


      {/* Partners (deferred) */}
      <Container htmlFor="ultra-wide">
        <div id={HOME_PAGE_SECTION_IDS.partners} className="py-12 scroll-mt-28">
          <Suspense fallback={<PartnersSkeleton />}>
            <Await resolve={loaderData.partners}>
              {(partners) => (
                partners.length > 0 &&
                <ProviderSliderSection
                  providers={partners}
                  heading="Explore Offices from Our Premium Partners"
                  description={"Access exclusive workspaces from top global partners."}
                  className="max-w-min"
                />
              )}
            </Await>
          </Suspense>
        </div>
      </Container>

      <LazyLoader>
        <Suspense fallback={<ListingsSkeleton />}>
          <Await resolve={geoData()}>
            {(resolvedGeo) => {
              let currency = resolvedGeo?.[0]?.currencyIsoCode || initialUserCurrency;
              return (
                <div id={HOME_PAGE_SECTION_IDS.listOnOfficeHub} className="scroll-mt-28">
                  <Container htmlFor='ultra-wide' className='pb-12'>
                    <ListOnOfficeHub
                      isMobile={false}
                      initialGeoData={resolvedGeo}
                      initialCity={city}
                      initialState={state}
                      initialLocation={countryCode}
                      initialUserCurrency={currency}
                      parsedQuery={parsedQuery}
                      regionData={regionData}
                      headingAs="h2"
                      staticTitle={`List Your Office Space in ${getFormattedCountryName(region)}`}
                    />
                  </Container>
                </div>
              );
            }}
          </Await>
        </Suspense >
      </LazyLoader >

      <Suspense fallback={<AboutSectionSkeleton />}>
        <Await resolve={loaderData.homeSummary}>
          {(homeSummary) => (
            <AboutSection mobile={false} region={region} country={countryName} />
          )}
        </Await>
      </Suspense>

      <Container htmlFor="ultra-wide">
        <GoogleReviews country={getCountryName(region)} isMobile={false} />
      </Container>

      <Suspense fallback={null}>
        <Await resolve={loaderData.faqs}>
          {(faqs) => faqs?.length ? (
            <Container htmlFor="ultra-wide">
              <div className="py-10">
                <FAQs title={`Answers to Common Office Space Queries in ${getFormattedCountryName(region)}`} faqs={faqs} />
              </div>
            </Container>
          ) : null}
        </Await>
      </Suspense>

      <Suspense fallback={<BlogsSkeleton />}>
        <Await resolve={loaderData.blogs}>
          {(blogs) => blogs.blogs.length > 5 ? (
            <div className="py-10 bg-gray-50">
              <GuideAndTips blogs={blogs.blogs} region={getCountryName(region)} />
            </div>
          ) : null}
        </Await>
      </Suspense>

      <OfficeSearchBanner country={region} />
      {/* <Suspense fallback={<div>Loading Spaces...</div>}>
        <Await resolve={loaderData.cities}>
          {(states) =>
            states.results.length ?
              <Container htmlFor='ultra-wide'>

                <BrowseOfficeSpaces data={states} region={region} />
              </Container> : null
          }
        </Await>
      </Suspense> */}

    </div >
  )
}

export default DesktopLayout